import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { EduShell, StatCard, StatusBadge, EmptyState, TEACHER_NAV } from "@/components/EduShell";
import { eduRpc, formatDue } from "@/lib/education";
import { useAuth } from "@/hooks/useAuth";

import { T } from "@/components/T";
import i18n from "@/i18n";
export const Route = createFileRoute("/teacher/")({
  component: TeacherDashboard,
  head: () => ({
    meta: [
      { title: "Teacher dashboard - Inquira" },
      { name: "description", content: "See classes, assignments due, submissions to review and class performance at a glance." },
      { property: "og:title", content: "Inquira teacher dashboard" },
      { property: "og:description", content: "Track classes, assignments and student progress in one place." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

interface Overview {
  classes: number;
  students: number;
  assignments_due: number;
  needs_review: number;
  average_score: number | null;
  recent_submissions: Array<{ id: string; assignment_id: string; title: string; student_name: string; status: string; score: number | null; submitted_at: string | null }>;
  upcoming: Array<{ id: string; title: string; due_at: string | null }>;
  needs_attention: Array<{ student_id: string; student_name: string; overdue: number }>;
}

function TeacherDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<Overview | null>(null);

  useEffect(() => {
    if (!user) return;
    void eduRpc<Overview>("edu_teacher_overview").then(({ data }) => setData(data));
  }, [user]);

  return (
    <EduShell
      nav={TEACHER_NAV}
      title={i18n.t("edu.teacher_index.teacher_dashboard")}
      subtitle={i18n.t("edu.teacher_index.your_classes_deadlines_and_work_waiting_")}
      actions={
        <>
          <Link to="/teacher/classes" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            New class
          </Link>
          <Link to="/teacher/assignments/new" search={{ classId: undefined }} className="rounded-full border border-rule bg-card px-4 py-2 text-sm font-semibold text-ink">
            New assignment
          </Link>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label={i18n.t("edu.teacher_index.students")} value={data?.students ?? "—"} />
        <StatCard label={i18n.t("edu.teacher_index.active_classes")} value={data?.classes ?? "—"} />
        <StatCard label={i18n.t("edu.teacher_index.due_soon")} value={data?.assignments_due ?? "—"} />
        <StatCard label={i18n.t("edu.teacher_index.needs_review")} value={data?.needs_review ?? "—"} />
        <StatCard label={i18n.t("edu.teacher_index.average_score")} value={data?.average_score ?? "—"} hint="Across graded work" />
      </div>

      <section className="mt-8 grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.teacher_index.recent_submissions" /></h2>
          {data && data.recent_submissions.length > 0 ? (
            <ul className="space-y-2">
              {data.recent_submissions.map((s) => (
                <li key={s.id} className="rounded-2xl border border-rule bg-card p-3">
                  <Link to="/teacher/assignments/$assignmentId" params={{ assignmentId: s.assignment_id }} className="block">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ink">{s.student_name}</span>
                      <StatusBadge status={s.status} />
                    </div>
                    <p className="text-sm text-ink-muted">{s.title}</p>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={i18n.t("edu.teacher_index.no_submissions_yet")} body="Published assignments will show student work here." />
          )}
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.teacher_index.upcoming_deadlines" /></h2>
            {data && data.upcoming.length > 0 ? (
              <ul className="space-y-2">
                {data.upcoming.map((a) => (
                  <li key={a.id} className="flex items-center justify-between rounded-2xl border border-rule bg-card p-3">
                    <span className="font-semibold text-ink">{a.title}</span>
                    <span className="text-xs text-ink-muted">{formatDue(a.due_at)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title={i18n.t("edu.teacher_index.nothing_due")} />
            )}
          </div>
          <div>
            <h2 className="mb-3 font-display text-lg font-bold text-ink"><T k="edu.teacher_index.may_need_attention" /></h2>
            {data && data.needs_attention.length > 0 ? (
              <ul className="space-y-2">
                {data.needs_attention.map((s) => (
                  <li key={s.student_id} className="flex items-center justify-between rounded-2xl border border-rule bg-card p-3">
                    <span className="font-semibold text-ink">{s.student_name}</span>
                    <span className="text-xs text-ink-muted">
                      {s.overdue} assignment{s.overdue === 1 ? "" : "s"} overdue
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title={i18n.t("edu.teacher_index.everyone_is_up_to_date")} />
            )}
          </div>
        </div>
      </section>
    </EduShell>
  );
}
